import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, Sector } from 'recharts';
import styles from './OverviewTabContent.module.css';
import { fetchUpcomingInstallments, fetchExpectedInsight, fetchRealInsight, exportInsights } from '../services/insightService';
import { searchProjects, fetchProjectQuarterInsight } from '../services/projectService';
import { useHasPermission } from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';

const cashflowOptions = [
  { label: '12 Months', key: '12m' },
  { label: '6 Months', key: '6m' },
  { label: '30 Days', key: '30d' },
  { label: '7 Days', key: '7d' },
];

const quarterColors = {
  Q1: '#AA4C0A',
  Q2: '#E08600',
  Q3: '#EFBF38',
  Q4: '#F5DE7A'
};


const formatMoneyShort = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '0';
  const abs = Math.abs(Number(value));
  if (abs >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return Number(value).toLocaleString();
};

const formatMoneyFull = (value) => `${Number(value || 0).toLocaleString()} EGP`;

const getChartMoneyValues = (data) => (
  data.flatMap(item => [item.expected, item.real]).filter(v => typeof v === 'number' && !Number.isNaN(v))
);

const getYAxisDomain = (data) => {
  const values = getChartMoneyValues(data);
  if (!values.length) return [0, 1000];

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const spread = Math.max(1, maxValue - minValue);
  const padding = spread * 0.1;

  const lowerBound = Math.max(0, minValue - padding);
  const upperBound = maxValue + padding;

  return [Math.floor(lowerBound), Math.ceil(upperBound)];
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.customTooltipContainer}>
      <div className={styles.customTooltipBox}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {payload.map((entry, idx) => (
          <div key={idx} style={{ marginTop: 4, color: entry.color }}>
            {entry.name}: <span style={{ fontWeight: 700 }}>{entry.value.toLocaleString()} EGP</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OverviewTabContent() {
  // Permission checks
  const hasExportInsight = useHasPermission(PERMISSIONS.EXPORT_INSIGHT);

  const [cashflowFilter, setCashflowFilter] = useState(cashflowOptions[0].key);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useDateRange, setUseDateRange] = useState(false);
  const [expectedData, setExpectedData] = useState([]);
  const [realData, setRealData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [upcomingInstallments, setUpcomingInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Project quarter insights states
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [quarterInsights, setQuarterInsights] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);

  const handleApplyDateRange = async () => {

    console.log('Applying date range:', { startDate, endDate });

    setChartLoading(true);
    setChartError(null);

    try {
      const expectedRes = await fetchExpectedInsight(null, { start_date: startDate, end_date: endDate });
      const realRes = await fetchRealInsight(null, { start_date: startDate, end_date: endDate });

      setExpectedData(expectedRes.data || []);
      setRealData(realRes.data || []);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setChartError(err.message || 'Failed to fetch chart data');
      setExpectedData([]);
      setRealData([]);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    const fetchChartData = async () => {
      setChartLoading(true);
      setChartError(null);

      try {
        const expectedRes = await fetchExpectedInsight(cashflowFilter);
        const realRes = await fetchRealInsight(cashflowFilter);

        setExpectedData(expectedRes.data || []);
        setRealData(realRes.data || []);
      } catch (err) {
        setChartError(err.message || 'Failed to fetch chart data');
        setExpectedData([]);
        setRealData([]);
      } finally {
        setChartLoading(false);
      }
    };

    if (!useDateRange) {
      fetchChartData();
    }
  }, [cashflowFilter, useDateRange]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchUpcomingInstallments()
      .then((res) => {
        setUpcomingInstallments(res.data || []);
      })
      .catch((err) => {
        setError(err.message || 'Failed to fetch upcoming installments');
        setUpcomingInstallments([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Search projects when search query changes
  useEffect(() => {
    const searchProjectsAPI = async () => {
      if (projectSearchQuery.trim().length > 0) {
        setProjectLoading(true);
        try {
          const response = await searchProjects(projectSearchQuery);
          if (response.data && Array.isArray(response.data)) {
            setProjects(response.data);
          } else {
            setProjects([]);
          }
        } catch (error) {
          console.error('Failed to search projects:', error);
          setProjects([]);
        } finally {
          setProjectLoading(false);
        }
      } else {
        setProjects([]);
        setProjectLoading(false);
      }
    };

    const timeoutId = setTimeout(searchProjectsAPI, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [projectSearchQuery]);

  // Fetch quarter insights when project is selected
  useEffect(() => {
    if (selectedProject) {
      setInsightLoading(true);
      fetchProjectQuarterInsight(selectedProject._id)
        .then((res) => {
          setQuarterInsights(res.data);
        })
        .catch((err) => {
          console.error('Failed to fetch quarter insights:', err);
          setQuarterInsights(null);
        })
        .finally(() => setInsightLoading(false));
    }
  }, [selectedProject]);

  // Merge expected and real data by date for the chart
  const mergedChartData = (() => {
    const map = new Map();
    expectedData.forEach(item => {
      map.set(item.date, { date: item.date, expected: item.totalAmount, real: null });
    });
    realData.forEach(item => {
      if (map.has(item.date)) {
        map.get(item.date).real = item.totalAmount;
      } else {
        map.set(item.date, { date: item.date, expected: null, real: item.totalAmount });
      }
    });
    // Sort by date ascending
    return Array.from(map.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  })();

  // Prepare donut chart data from quarter insights
  const quarterChartData = quarterInsights
    ? Object.entries(quarterInsights.quarter_percentage).map(([quarter, percentage]) => {
      const totalAmount = quarterInsights.quarter_amounts?.[quarter] ?? 0;
      const paidAmount = quarterInsights.paid_quarters?.[quarter] ?? 0;
      return {
        name: quarter,
        value: parseFloat(percentage.replace('%', '')),
        totalAmount,
        paidAmount,
        paidRatio: totalAmount > 0 ? paidAmount / totalAmount : 0,
      };
    })
    : [];

  const renderFillLabel = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, payload } = props;
    if (!payload) return null;
    const { paidRatio, paidAmount, totalAmount } = payload;

    const ratio = paidRatio || 0;
    const paidEndAngle = startAngle + (endAngle - startAngle) * ratio;
    const midAngle = (startAngle + endAngle) / 2;
    const RADIAN = Math.PI / 180;
    const labelRadius = innerRadius + (outerRadius - innerRadius) / 2;
    const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const showLabel = Math.abs(endAngle - startAngle) > 20;

    return (
      <g>
        {ratio > 0 && ratio < 0.999 && (
          <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={paidEndAngle} endAngle={endAngle} fill="rgba(0,0,0,0.45)" />
        )}
        {showLabel && (
          <>
            <text x={labelX} y={labelY - 9} fill="#fff" stroke="#000" strokeWidth="3" paintOrder="stroke" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight="bold">
              {(paidAmount ?? 0).toLocaleString()}
            </text>
            <text x={labelX} y={labelY + 9} fill="rgba(255,255,255,0.8)" stroke="#000" strokeWidth="2" paintOrder="stroke" textAnchor="middle" dominantBaseline="central" fontSize={9}>
              {(totalAmount ?? 0).toLocaleString()}
            </text>
          </>
        )}
      </g>
    );
  };

  const chartValues = getChartMoneyValues(mergedChartData);
  const yAxisDomain = getYAxisDomain(mergedChartData);
  const minChartValue = chartValues.length ? Math.min(...chartValues) : 0;
  const maxChartValue = chartValues.length ? Math.max(...chartValues) : 0;

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setIsProjectDropdownOpen(false);
    setProjectSearchQuery('');
    setProjects([]);
  };

  const handleExportInsights = async () => {
    setExportLoading(true);
    try {
      await exportInsights();
    } catch (error) {
      console.error('Error exporting insights:', error);
      alert('Failed to export insights: ' + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="overview-content">
      <div className={styles.overviewHeader}>
        <span className={styles.overviewGreeting}>Hey Swag -</span>
        <span className={styles.overviewSubtext}> here's what's happening with your business today</span>
      </div>
      <div className={styles.cashflowSection}>
        <div className={styles.cashflowTabsRow}>
          <div className={styles.filterControls}>
            <div className={styles.dateRangeToggle}>
              <label>
                <input
                  type="checkbox"
                  checked={useDateRange}
                  onChange={(e) => setUseDateRange(e.target.checked)}
                />
                Use Date Range
              </label>
            </div>

            {useDateRange ? (
              <div className={styles.dateRangeControls}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={styles.dateInput}
                  max={endDate || undefined}
                />
                <span>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={styles.dateInput}
                  min={startDate || undefined}
                />
                <button
                  onClick={handleApplyDateRange}
                  disabled={!startDate || !endDate}
                  className={styles.applyButton}
                >
                  Apply
                </button>
              </div>
            ) : (
              <div className={styles.cashflowTabs}>
                {cashflowOptions.map(opt => (
                  <button
                    key={opt.key}
                    className={
                      cashflowFilter === opt.key
                        ? `${styles.cashflowTab} ${styles.cashflowTabActive}`
                        : styles.cashflowTab
                    }
                    onClick={() => setCashflowFilter(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {hasExportInsight && (
            <button
              className={styles.exportButton}
              onClick={handleExportInsights}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <>
                  <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export
                </>
              )}
            </button>
          )}
        </div>
        <div className="cashflow-graph-container">
          {chartLoading ? (
            <div style={{ color: '#fff', padding: 32 }}>Loading chart...</div>
          ) : chartError ? (
            <div style={{ color: 'red', padding: 32 }}>{chartError}</div>
          ) : (
            <>

              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={mergedChartData} margin={{ top: 20, right: 30, left: 16, bottom: 0 }}>
                  <CartesianGrid stroke="#333" strokeDasharray="0" vertical={false} />
                  <XAxis dataKey="date" stroke="#aaa" />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#FABD08', strokeWidth: 2, opacity: 0.2 }} position={getTooltipPosition} />
                  <Line type="monotone" dataKey="expected" name="Expected" stroke="#FABD08" strokeWidth={3} dot={{ r: 5 }} />
                  <Line type="monotone" dataKey="real" name="Real" stroke="#1976d2" strokeWidth={3} dot={{ r: 5 }} />
                  <YAxis stroke="#aaa" domain={yAxisDomain} tickCount={6} width={72} tickFormatter={formatMoneyShort} />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </div>

      <div className={styles.bottomSection}>
        <div className={styles.installmentsSection}>
          <div className={styles.upcomingInstallmentsHeader}>Up Coming Installments</div>
          <div className={styles.installmentsList}>
            {loading ? (
              <div className={styles.installmentRow}><span>Loading...</span></div>
            ) : error ? (
              <div className={styles.installmentRow}><span style={{ color: 'red' }}>{error}</span></div>
            ) : upcomingInstallments.length === 0 ? (
              <div className={styles.installmentRow}><span>No upcoming installments</span></div>
            ) : (
              upcomingInstallments.map((item, idx) => (
                <div key={item.id} className={styles.installmentRow}>
                  <span className={styles.waitingLabel}>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ marginRight: 4, verticalAlign: 'middle' }} xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="8" stroke="white" strokeWidth="2" fill="none" />
                      <path d="M10 6V10L12.5 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Waiting
                  </span>
                  <div className={styles.ownerCol} title={item.user}>
                    <div className={styles.customerName}>{item.user}</div>
                    {item.swag_id && (
                      <div className={styles.swagId}>SWAG ID: {item.swag_id}</div>
                    )}
                  </div>
                  <div className={styles.amountDateCol}>
                    <span className={styles.amount}>{item.amount}</span>
                    <span className={styles.date}>{item.date}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={styles.quarterInsightsSection}>
          <div className={styles.quarterInsightsHeader}>Project Quarter Insights</div>

          <div className={styles.projectSearchContainer}>
            <div
              className={styles.projectSearchDropdown}
              onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
            >
              <div className={styles.projectSearchTrigger}>
                <span className={styles.projectSearchValue}>
                  {selectedProject ? selectedProject.name : 'Search for project...'}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transform: isProjectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </div>

              {isProjectDropdownOpen && (
                <div className={styles.projectDropdownMenu}>
                  <div className={styles.projectSearchInputContainer}>
                    <div className={styles.searchInputWrapper}>
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={projectSearchQuery}
                        onChange={(e) => setProjectSearchQuery(e.target.value)}
                        className={styles.projectSearchInput}
                        autoFocus
                      />
                      {projectLoading && (
                        <div className={styles.searchLoadingSpinner}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" className={styles.spinnerPath} />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.projectOptionsList}>
                    {projectLoading ? (
                      <div className={styles.projectLoadingMessage}>Searching...</div>
                    ) : projects.length > 0 ? (
                      projects.map(project => (
                        <div
                          key={project._id}
                          className={styles.projectOption}
                          onClick={() => handleProjectSelect(project)}
                        >
                          <div className={styles.projectInfo}>
                            <div className={styles.projectName}>{project.name}</div>
                            <div className={styles.projectLocation}>{project.location}</div>
                          </div>
                        </div>
                      ))
                    ) : projectSearchQuery.trim() ? (
                      <div className={styles.projectNoResults}>No projects found for "{projectSearchQuery}"</div>
                    ) : (
                      <div className={styles.projectNoResults}>Start typing to search projects...</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedProject && (
            <div className={styles.quarterChartContainer}>
              {insightLoading ? (
                <div className={styles.chartLoading}>Loading insights...</div>
              ) : quarterInsights ? (
                <div className={styles.donutChartWrapper}>
                  <div className={styles.chartYearLabel}>{new Date().getFullYear()}</div>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={quarterChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={130}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                        label={renderFillLabel}
                        labelLine={false}
                      >
                        {quarterChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={quarterColors[entry.name]} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip formatter={(_value, _name, props) => [`${(props.payload.totalAmount ?? 0).toLocaleString()}`, props.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>

                  <div className={styles.totalProfitCenter}>
                    <div className={styles.totalProfitLabel}>Total Profit</div>
                    <div className={styles.totalProfitValue}>
                      {quarterInsights.total_profit.toLocaleString()}
                    </div>
                  </div>

                  <div className={styles.quarterLegend}>
                    {Object.entries(quarterInsights.quarter_percentage).map(([quarter, percentage]) => (
                      <div key={quarter} className={styles.legendItem}>
                        <div
                          className={styles.legendColor}
                          style={{ backgroundColor: quarterColors[quarter] }}
                        ></div>
                        <span className={styles.legendText}>
                          {quarter}: {percentage}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.chartError}>Failed to load quarter insights</div>
              )}
            </div>
          )}

          {!selectedProject && (
            <div className={styles.emptyStateContainer}>
              <div className={styles.emptyStateIcon}>📊</div>
              <div className={styles.emptyStateTitle}>Select a Project</div>
              <div className={styles.emptyStateDescription}>
                Choose a project from the search dropdown above to view quarterly insights and profit distribution.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getTooltipPosition() {
  return { y: 0 };
} 